import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import EventCard from '@/components/feature/EventCard';
import { SectionHead } from '@/components/feature/Atoms';
import { events, columns } from '@/mocks/events';
import { usePageMeta } from '@/hooks/useSeo';

const collectionSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '99吃瓜 · 复盘档案',
  description: '按栏目归档的事件复盘与反转梳理简报，可筛选与检索。',
};

export default function Review() {
  usePageMeta(
    '复盘档案 - 99吃瓜事件复盘与反转梳理目录',
    '99吃瓜复盘档案按栏目归档事件复盘、反转梳理、各方回应、进展追踪与吃瓜法务内容，支持关键词检索与栏目筛选。',
  );

  const [column, setColumn] = useState('全部');
  const [query, setQuery] = useState('');

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((e) => {
      const matchCol = column === '全部' || e.kicker === column;
      const matchQ =
        !q ||
        e.title.toLowerCase().includes(q) ||
        e.summary.toLowerCase().includes(q) ||
        e.tags.some((t) => t.toLowerCase().includes(q)) ||
        e.kicker.includes(q);
      return matchCol && matchQ;
    });
  }, [column, query]);

  return (
    <div className="py-12 md:py-16">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionSchema) }} />

      <nav aria-label="面包屑" className="mx-auto max-w-[1280px] px-4 md:px-6">
        <ol className="flex items-center gap-1.5 text-xs text-foreground-500">
          <li>
            <Link to="/" className="hover:text-primary-600">
              首页
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li aria-current="page" className="text-foreground-700">
            复盘档案
          </li>
        </ol>
      </nav>

      <div className="mx-auto mt-6 max-w-[1280px] px-4 md:px-6">
        <SectionHead
          kicker="复盘档案"
          title="事件复盘与反转梳理的完整目录"
          desc="档案按栏目归档，收录我们梳理过的每一期事件复盘、反转观察、回应拆解与进展追踪。点击条目可展开复盘要点。"
        />

        {/* 检索 + 筛选 */}
        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="按栏目筛选">
            {['全部', ...columns].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColumn(c)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  column === c
                    ? 'bg-primary-500 font-semibold text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-secondary-100'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <label className="flex max-w-md items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400 lg:w-72">
            <i className="ri-search-line text-foreground-500" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索事件 / 标签 / 关键词…"
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              aria-label="搜索复盘内容"
            />
          </label>
        </div>

        <p className="mt-4 text-xs text-foreground-500">
          当前显示 <strong className="text-foreground-800">{list.length}</strong> 条{column !== '全部' ? `「${column}」` : ''}档案
          {query.trim() ? `，匹配「${query.trim()}」` : ''}
        </p>

        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((e) => (
            <EventCard key={e.slug} item={e} />
          ))}
        </div>

        {list.length === 0 && (
          <div className="mt-10 flex flex-col items-center rounded-lg border border-background-200 bg-background-100 py-16 text-center">
            <i className="ri-inbox-2-line text-4xl text-background-400" aria-hidden="true" />
            <p className="mt-4 text-sm text-foreground-600">没有找到匹配的复盘档案，换个关键词试试。</p>
            <button
              type="button"
              onClick={() => {
                setColumn('全部');
                setQuery('');
              }}
              className="mt-4 inline-flex items-center whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              清除筛选条件
            </button>
          </div>
        )}
      </div>
    </div>
  );
}